import classNames from 'classnames'
import { EditorState, Plugin, PluginKey, Transaction } from 'prosemirror-state'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'
import { Node } from 'prosemirror-model'
import isEqual from 'lodash/isEqual'

import { userName } from 'common/PresenceAvatars/PresenceAvatars'
import { consolidatedUserId as userId } from 'common/PresenceAvatars/consolidatedUserId'
import { PresenceCursor, getSelectionClass } from 'common/PresenceCursor/PresenceCursor'
import { PresenceOutOfView } from 'common/PresenceOutOfView/PresenceOutOfView'
import Styles from './InlinePresence.module.scss'
import ProseEditStyles from 'screens/ProseEdit/ProseEdit.module.scss'
import { ElementPresence, PresenceUser, UsersMetadata } from 'types'
import { getNextTextPosition } from 'common/Prose/utils'
import { schema } from 'prose-core'
import { createRoot, Root } from 'react-dom/client'

interface InlinePresenceState {
  decorations: DecorationSet<any>
  presences: ElementPresence[]
  followedUser: PresenceUser | null
}

export const InlinePresencePluginKey = new PluginKey('inlinePresence')

// Utilities for Inline cursors
const inlineTextCursorWidget = ({
  pos,
  color,
  name,
  id,
}: {
  pos: number
  color: string | undefined
  name: string
  id: number
}): Decoration => {
  const container = document.createElement('span')
  const root = createRoot(container)
  container.dataset.presenceCursor = 'true'
  container.dataset.userId = id.toString()
  container.className = classNames(Styles.cursorContainer)
  root.render(<PresenceCursor color={color} name={name} />)
  return Decoration.widget(pos, container, {
    root,
    userId: id,
    key: btoa(unescape(encodeURIComponent(`${pos}|${name}|${color}`))),
    destroy: function () {
      this.root.unmount()
    },
  })
}

const inCodeBlock = ({ pos, doc }: { pos: number; doc: Node }): boolean => {
  try {
    const resolvedPos = doc.resolve(pos)
    const start = resolvedPos.before(1)
    const { nodeAfter } = doc.resolve(start)

    return !!(nodeAfter && nodeAfter.type === schema.nodes.code_block)
  } catch (error) {
    // doc.resolve(pos) will throw a range error if pos is larger than last pos in doc
    if (error instanceof RangeError) {
      return false
    }
    throw error
  }
}

// Define where to position the inline cursor. In most cases, we want it to be
// wherever the user's cursor is (pos input). But if the user has selected
// something else than text, for example: an image, a table, a card, etc
// then the pos is not inside some text and the rendering will be ugly.
// In that case, find the next text position.
// Those objects are defined by the resolvedPos nodeBefore except for table_cell
// selections which will be defined by the resolvedPos nodeAfter.
const getCursorPos = ({ pos, doc }: { pos: number; doc: Node }): number => {
  try {
    const resolvedPos = doc.resolve(pos)

    // Cursor in code block: what we should do is to add a codemirror widget.
    // But it's very involved, so a mitigation right now is to show the cursor
    // after the code block (like we do for images, and other not-in-text pos)
    if (inCodeBlock({ pos, doc })) return resolvedPos.after(1) + 1

    const { nodeBefore, nodeAfter } = resolvedPos
    const tableCellSelected = nodeAfter && nodeAfter.type === schema.nodes.table_cell

    if (!nodeBefore && !tableCellSelected) return pos
    if (nodeBefore && nodeBefore.type === schema.nodes.text) return pos

    return getNextTextPosition(resolvedPos).pos
  } catch (error) {
    // doc.resolve(pos) will throw a range error if pos is larger than last pos in doc
    // in that case, render last pos in doc
    // this can happen for example if user A has cursor in a block of code, and
    // user B deletes that block of code and everything below. so now, user B gets
    // a pos for user A that is out of the doc
    if (error instanceof RangeError) {
      return doc.content.size
    }
    throw error
  }
}

const buildDecorations = ({
  doc,
  presences,
  followedUser,
}: {
  doc: Node
  presences: ElementPresence[]
  followedUser: PresenceUser | null
}): InlinePresenceState => {
  const widgets: Decoration[] = []

  presences.forEach((presence) => {
    const { head, anchor, color, user, anonymous_user } = presence

    if (head) {
      const pos = getCursorPos({ pos: head, doc })
      const widget = inlineTextCursorWidget({
        pos,
        color,
        id: userId(user || anonymous_user),
        name: userName(user || anonymous_user),
      })
      widgets.push(widget)
    }

    if (head && anchor && head !== anchor) {
      widgets.push(
        Decoration.inline(
          Math.min(head, anchor),
          Math.max(head, anchor),
          { class: getSelectionClass(color) },
          { userId: userId(user || anonymous_user) },
        ),
      )
    }
  })

  return {
    decorations: DecorationSet.create(doc, widgets),
    presences: presences.filter((presence) => presence.head),
    followedUser,
  }
}

const adjustStateToDoc = ({ tr, state }: { tr: Transaction; state: InlinePresenceState }) => {
  const decorations = state.decorations.map(tr.mapping, tr.doc)

  const presences = state.presences.map((presence) => {
    const { user, anonymous_user } = presence
    const id = userId(user || anonymous_user)
    const widgets = decorations.find(undefined, undefined, ({ userId }) => userId === id)

    // has selection, head !== anchor
    if (widgets.length === 2) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const head = widgets.find(({ from, to }) => from === to)!.from

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { from, to } = widgets.find(({ from, to }) => from !== to)!
      const anchor = from === head ? to : from

      return { ...presence, head, anchor }
    }

    // no selection, head === anchor
    if (widgets.length === 1) {
      return { ...presence, head: widgets[0].from, anchor: widgets[0].from }
    }

    return presence
  })

  return { ...state, presences, decorations }
}

// Utilities for Out of view presences
interface UsersById {
  [userId: string]: PresenceUser
}

interface UsersOutOfView {
  users: UsersById
  metadata: UsersMetadata
}

interface TopAndBottomUsersOutOfView {
  top: UsersOutOfView
  bottom: UsersOutOfView
}

const createOutOfViewContainer = (direction: 'top' | 'bottom') => {
  const container = document.createElement('div')
  container.className = classNames(
    ProseEditStyles.presencesOutOfViewContainer,
    Styles.outOfViewContainer,
    Styles[`direction-${direction}`],
  )
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  document.getElementById('main-content')!.append(container)
  const root = createRoot(container)
  return root
}

const assignOutOfView = ({
  data,
  id,
  entry,
  presence,
}: {
  data: UsersOutOfView
  id: string
  entry: IntersectionObserverEntry
  presence: ElementPresence
}) => {
  data.users[id] = presence.user || presence.anonymous_user
  data.metadata[id] = {
    color: presence.color,
    present: true,
    onClick: (event) => {
      event.preventDefault()

      entry.target.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },
  }
}

const renderOutOfViewAvatars = ({
  users,
  metadata,
  direction,
  root,
}: {
  users: UsersById
  metadata: UsersMetadata
  direction: 'top' | 'bottom'
  root: Root
}) => {
  const nonNullUsers = Object.values(users).filter((user) => user)
  root.render(<PresenceOutOfView metadata={metadata} users={nonNullUsers} direction={direction} />)
}

const removeUserFromOutOfView = ({
  id,
  usersOutOfView,
}: {
  id: string
  usersOutOfView: TopAndBottomUsersOutOfView
}) => {
  delete usersOutOfView.top.users[id]
  delete usersOutOfView.top.metadata[id]
  delete usersOutOfView.bottom.users[id]
  delete usersOutOfView.bottom.metadata[id]
}

const triageOutOfView = ({
  entries,
  view,
  usersOutOfView,
  topContainer,
  bottomContainer,
}: {
  entries: IntersectionObserverEntry[]
  view: EditorView
  usersOutOfView: TopAndBottomUsersOutOfView
  topContainer: Root
  bottomContainer: Root
}) => {
  const { top, bottom } = usersOutOfView
  const presences = InlinePresencePluginKey.getState(view.state).presences as ElementPresence[]
  const followedUser = InlinePresencePluginKey.getState(view.state).followedUser

  entries.forEach((entry) => {
    const id = entry.target.getAttribute('data-user-id')

    // Center followed cursor if applicable
    if (`${followedUser?.id}` === id) {
      entry.target.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }

    if (id) {
      if (entry.isIntersecting) {
        removeUserFromOutOfView({ usersOutOfView, id })
      } else {
        const presence = presences.find(({ user, anonymous_user }) => userId(user || anonymous_user).toString() === id)
        if (presence) {
          const { y } = entry.boundingClientRect
          // It seems like there is a 'weird'™ prosemirror behavior: when drawing decorations,
          // they seem to first be at position: {y: 0} and then get moved to where they
          // need to be. So there is 2 events and the first one has {y: 0} and the next
          // one has { y: <real value> }
          // because of that, we ignore any case where y === 0
          if (y !== 0) {
            const data = y < window.innerHeight ? top : bottom

            removeUserFromOutOfView({ usersOutOfView, id })
            assignOutOfView({ entry, id, presence, data })
          }
        }
      }
    }
  })

  renderOutOfViewAvatars({ ...top, direction: 'top', root: topContainer })
  renderOutOfViewAvatars({ ...bottom, direction: 'bottom', root: bottomContainer })
}

const getRidOfGhostUsers = ({
  state,
  usersOutOfView,
  topContainer,
  bottomContainer,
}: {
  state: EditorState
  usersOutOfView: TopAndBottomUsersOutOfView
  topContainer: Root
  bottomContainer: Root
}) => {
  const { top, bottom } = usersOutOfView
  const presences = InlinePresencePluginKey.getState(state).presences as ElementPresence[]

  const userIds = presences.map(({ user, anonymous_user }) => userId(user || anonymous_user).toString())

  const removables: (UsersById | UsersMetadata)[] = [bottom.users, bottom.metadata, top.users, top.metadata]

  removables.forEach((data) => {
    Object.keys(data).forEach((id) => {
      if (!userIds.includes(id)) {
        delete data[id]
      }
    })
  })

  renderOutOfViewAvatars({ ...top, direction: 'top', root: topContainer })
  renderOutOfViewAvatars({ ...bottom, direction: 'bottom', root: bottomContainer })
}

export const InlinePresencePlugin = (layerId: string) =>
  new Plugin({
    key: InlinePresencePluginKey,
    state: {
      init(config): InlinePresenceState {
        return {
          decorations: DecorationSet.create(config.doc, []),
          presences: [],
          followedUser: null,
        }
      },
      apply(tr: Transaction, state: InlinePresenceState) {
        const presences: ElementPresence[] | undefined = tr.getMeta(InlinePresencePluginKey)

        if (tr.getMeta('follow_user') !== undefined) {
          return { ...state, followedUser: tr.getMeta('follow_user') }
        }

        const layerPresences = presences?.filter((p) => p.layer_id === layerId)
        if (layerPresences && !isEqual(presences, state.presences)) {
          return buildDecorations({ doc: tr.doc, presences: layerPresences, followedUser: state.followedUser })
        }

        return adjustStateToDoc({ tr, state })
      },
    },
    props: {
      decorations(state) {
        return InlinePresencePluginKey.getState(state).decorations
      },
    },
    view: (view: EditorView) => {
      // jest does not like IntersectionObserver, in that case ignore the whole
      // 'users out of view' feature. Same apply to browsers not supporting it BTW
      // (but there shouldn't be any that we support)
      if (!('IntersectionObserver' in window)) return {}

      const bottomContainer = createOutOfViewContainer('bottom')
      const topContainer = createOutOfViewContainer('top')

      const usersOutOfView: TopAndBottomUsersOutOfView = {
        top: { users: {}, metadata: {} },
        bottom: { users: {}, metadata: {} },
      }

      const onIntersect = (entries: IntersectionObserverEntry[]) => {
        triageOutOfView({ entries, view, usersOutOfView, topContainer, bottomContainer })
      }

      const observer = new IntersectionObserver(onIntersect)

      return {
        update: (view: EditorView) => {
          const { state } = view
          getRidOfGhostUsers({ state, usersOutOfView, topContainer, bottomContainer })

          document.querySelectorAll('[data-presence-cursor="true"]').forEach((el) => {
            observer.observe(el)
          })
        },

        destroy: () => {
          observer.disconnect()
          setTimeout(() => {
            bottomContainer.unmount()
            topContainer.unmount()
          }, 0)
        },
      }
    },
  })
